import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Lock, User, Eye, EyeOff, KeyRound } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REDIRECT_URL = encodeURIComponent(typeof window !== 'undefined' ? window.location.origin : '');
const OAUTH_BASE_URL = process.env.REACT_APP_OAUTH_URL || "https://auth.emergentagent.com";
const AUTH_URL = `${OAUTH_BASE_URL}/?redirect=${REDIRECT_URL}`;

const GoogleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

export default function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    clubAccessCode: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        club_access_code: formData.clubAccessCode,
      }, { withCredentials: true });
      
      // If admin approval is required, don't try to navigate — no session
      // cookie has been set. Show a friendly waiting-room screen instead.
      if (res.data?.pending_approval) {
        setPendingApproval(true);
        return;
      }
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-app flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header — modern wordmark */}
        <div className="text-center mb-8">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2.5 mb-6 hover:opacity-80 transition-opacity"
            data-testid="brand-home-link"
          >
            <Logo variant="icon" size={32} />
            <span className="text-lg font-semibold tracking-tight text-ink">Golf Eclectic</span>
          </button>
          <p className="ge-eyebrow mb-2">Get started</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em] text-ink leading-[1.1]">
            Build your <span className="text-accent">perfect round</span>.
          </h1>
          <p className="text-ink-muted mt-3 text-[14.5px]">Join Golf Eclectic and start tracking your scores.</p>
        </div>

        <Card>
          <CardContent className="pt-6">
            {pendingApproval ? (
              // Waiting-room screen shown after a signup without a valid
              // club access code. No session, no navigation — the user has
              // to wait for an admin to click Approve.
              <div data-testid="pending-approval-screen" className="text-center py-6">
                <div className="mx-auto w-14 h-14 rounded-full bg-accent-tint flex items-center justify-center mb-4">
                  <KeyRound className="w-6 h-6 text-accent-deep" />
                </div>
                <h2 className="text-xl font-semibold text-ink mb-2">Awaiting admin approval</h2>
                <p className="text-ink-muted text-sm mb-6">
                  Your account has been created. Because you didn&apos;t enter a valid club access
                  code, an admin needs to approve you before you can sign in. You&apos;ll receive an
                  email once you&apos;re in.
                </p>
                <Button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="bg-ink hover:bg-ink-hover"
                  data-testid="pending-back-to-login-btn"
                >
                  Back to sign in
                </Button>
              </div>
            ) : (
            <>
            {/* Google sign-up — front and centre, single click */}
            <button
              type="button"
              onClick={() => (window.location.href = AUTH_URL)}
              data-testid="google-signin-btn"
              className="w-full inline-flex items-center justify-center gap-2.5 bg-surface hover:bg-app border border-line hover:border-line-strong rounded-xl py-3 text-[14.5px] font-medium text-ink transition-all"
            >
              <GoogleIcon />
              <span>Continue with Google</span>
            </button>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-line"></div>
              <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-soft">Or with email</span>
              <div className="flex-1 h-px bg-line"></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <Label htmlFor="name">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-3 h-5 w-5 text-ink-soft" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    className="pl-10"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-ink-soft" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    className="pl-10"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-ink-soft" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-ink-soft hover:text-ink-muted"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-xs text-ink-soft mt-1">
                  At least 8 characters, 1 uppercase, 1 number
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-ink-soft" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-3 text-ink-soft hover:text-ink-muted"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Club Access Code — required for auto-approval. Without a
                  valid code the account still gets created but an admin must
                  approve it before the user can log in. Fixes open-signup
                  abuse seen in production Feb 2026. */}
              <div>
                <Label htmlFor="clubAccessCode">Club access code</Label>
                <div className="relative mt-1">
                  <KeyRound className="absolute left-3 top-3 h-5 w-5 text-ink-soft" />
                  <Input
                    id="clubAccessCode"
                    data-testid="register-club-code-input"
                    type="text"
                    placeholder="Ask your club admin for this"
                    className="pl-10 uppercase tracking-widest"
                    value={formData.clubAccessCode}
                    onChange={(e) => setFormData({ ...formData, clubAccessCode: e.target.value })}
                    autoComplete="off"
                  />
                </div>
                <p className="text-xs text-ink-soft mt-1">
                  Get this from your club admin. Signup without a code still works but requires manual approval.
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-ink hover:bg-ink-hover py-6 text-[14.5px] font-semibold"
                disabled={loading}
              >
                {loading ? "Creating account…" : "Create account"}
              </Button>
            </form>

            {/* Sign In Link */}
            <div className="mt-6 pt-5 border-t border-line text-center">
              <p className="text-[13px] text-ink-muted">
                Already have an account?{" "}
                <button
                  onClick={() => navigate("/login")}
                  className="text-accent hover:text-accent-deep font-semibold"
                  data-testid="goto-login-link"
                >
                  Sign in
                </button>
              </p>
            </div>
            </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}